import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { commercialGet, commercialPost, commercialPut } from '../../utils/commercialApi';
import { detachKiosk } from '../../utils/kioskApi';
import { INK } from '../../utils/commercialColors';
import { kioskStatus, shortDuration } from '../../utils/kioskStatus';

// Built from kiosk-firmware/tools/ at every frontend build
// (scripts/build-kiosk-installer.js) and served with the site.
const INSTALLER_URL = `${process.env.PUBLIC_URL || ''}/downloads/qrproof-kiosk-installer.zip`;

// Rating kiosk setup for one facility, on its commercial page. Ambassadors
// install kiosks with the USB installer (kiosk-firmware/tools/), which needs
// the facility ID and a device key - this card issues that key. Backed by
// backend/routes/commercial/kiosk.ts, which only lets an ambassador act on
// facilities assigned to them.
export default function KioskCard({ facilityId, token }) {
  const [kiosk, setKiosk] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [idleMessage, setIdleMessage] = useState('');
  const [idleSaved, setIdleSaved] = useState(false);
  const [detached, setDetached] = useState(false);

  useEffect(() => {
    if (!token) return;
    commercialGet(`/facilities/${facilityId}/kiosk`, token)
      .then((data) => {
        setKiosk(data);
        setIdleMessage(data.idleMessage || '');
      })
      .catch((err) => setError(err.message));
  }, [facilityId, token]);

  const issueKey = async () => {
    if (kiosk?.hasKey && !window.confirm(
      'Une clé existe déjà. En générer une nouvelle déconnecte le kiosk déjà installé '
      + "jusqu'à ce qu'il soit reconfiguré avec la nouvelle clé. Continuer ?",
    )) return;
    setBusy(true);
    setError('');
    try {
      const data = await commercialPost(`/facilities/${facilityId}/kiosk/key`, token, {});
      setNewKey(data.deviceKey);
      setCopied(false);
      setKiosk((k) => ({ ...k, qrDisplayMode: 'device', hasKey: true }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const saveIdleMessage = async () => {
    setError('');
    try {
      await commercialPut(`/facilities/${facilityId}/kiosk/idle-message`, token, { idleMessage });
      setIdleSaved(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const detach = async () => {
    if (!window.confirm(
      'Détacher la borne de cet établissement ? Elle cessera de fonctionner ici d’ici quelques minutes '
      + 'et affichera son code d’association, pour être associée à un autre établissement.',
    )) return;
    setBusy(true);
    setError('');
    try {
      await detachKiosk(facilityId, token);
      setDetached(true);
      setNewKey('');
      setKiosk((k) => ({ ...k, hasKey: false }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const active = kiosk?.qrDisplayMode === 'device' && kiosk?.hasKey;

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Kiosk de notation</Typography>
        {kiosk && (
          <Chip size="small" label={active ? 'Activé' : 'Non activé'} color={active ? 'success' : 'default'} />
        )}
      </Stack>

      {active && <KioskHeartbeat kiosk={kiosk} />}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {detached && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Borne détachée : d’ici quelques minutes elle affichera son code d’association. Scannez-le depuis le
          nouvel établissement.
        </Alert>
      )}

      <Typography variant="body2" sx={{ color: INK.secondary, mb: 1 }}>
        <b>Avec un téléphone (recommandé)</b> : branchez la borne, donnez-lui le Wi-Fi avec l’app ESP BLE
        Provisioning, puis scannez son QR « Associer cette borne » avec l’appareil photo et choisissez cet
        établissement. Aucune clé à copier.
      </Typography>
      <Typography variant="body2" sx={{ color: INK.secondary, mb: 2 }}>
        <b>Par ordinateur</b> : générez une clé ici, puis lancez l'installateur USB avec l'ID de l'établissement
        (<b>{facilityId}</b>) et cette clé.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" size="small" component="a" href={INSTALLER_URL} download>
          Télécharger l’installateur (Mac / Windows)
        </Button>
        <Typography variant="caption" sx={{ display: 'block', color: INK.muted, mt: 0.5 }}>
          Mac : dézippez, puis clic droit sur « Installer kiosk.command » → Ouvrir → Ouvrir (la première fois
          seulement). Windows : double-cliquez « Installer kiosk.bat ». Le guide « LISEZMOI » est dans le dossier.
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="contained" onClick={issueKey} disabled={busy || !kiosk}>
          {kiosk?.hasKey ? 'Générer une nouvelle clé' : 'Activer le kiosk et générer la clé'}
        </Button>
        {kiosk?.hasKey && (
          <Button variant="outlined" color="error" onClick={detach} disabled={busy}>
            Détacher la borne
          </Button>
        )}
      </Stack>

      {newKey && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
            Copiez la clé maintenant : elle ne sera plus jamais affichée.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box component="code" sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, fontSize: 15, wordBreak: 'break-all' }}>
              {newKey}
            </Box>
            <Button size="small" variant="outlined" onClick={copyKey}>
              {copied ? 'Copié ✓' : 'Copier'}
            </Button>
          </Box>
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 3, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          fullWidth
          label="Message d'accueil du kiosk"
          placeholder="Ex. : Merci de votre visite, donnez-nous votre avis !"
          value={idleMessage}
          inputProps={{ maxLength: 255 }}
          onChange={(e) => { setIdleMessage(e.target.value); setIdleSaved(false); }}
        />
        <Button variant="outlined" onClick={saveIdleMessage} disabled={!kiosk}>
          {idleSaved ? 'Enregistré ✓' : 'Enregistrer'}
        </Button>
      </Box>
    </Paper>
  );
}

// Last heartbeat from the kiosk itself (GET /v1/device/config).
function KioskHeartbeat({ kiosk }) {
  const { state, minutes } = kioskStatus(kiosk.lastSeen);
  const label = {
    never: 'Kiosk jamais connecté',
    online: `Kiosk en ligne — vu il y a ${shortDuration(minutes ?? 0)}`,
    offline: `Kiosk hors ligne depuis ${shortDuration(minutes ?? 0)}`,
  }[state];
  const details = [kiosk.firmware && `firmware ${kiosk.firmware}`, kiosk.rssi != null && `Wi-Fi ${kiosk.rssi} dBm`]
    .filter(Boolean).join(' · ');
  return (
    <Typography variant="body2" sx={{ mb: 2, color: state === 'online' ? INK.success : state === 'offline' ? '#d03b3b' : INK.muted }}>
      {label}{details ? ` (${details})` : ''}
    </Typography>
  );
}
